import { ArrayUtility } from '@solcre-org/core';
import { BookingItemModel } from './booking-item.model';
import { CapacityModel } from './capacity.model';

export class VoyageSegmentModel {
  public id?: string;
  public availableCapacity: CapacityModel = new CapacityModel();
  public occupiedCapacity: CapacityModel = new CapacityModel();
  public occupiedPercentage: CapacityModel = new CapacityModel();
  public totalCapacity: CapacityModel = new CapacityModel();
  public bookingItems: BookingItemModel[] = [];

  constructor() {}

  /**
   * Choose which leg of a port call to show by default.
   *
   * A port call has an arrival leg (what is on board when the ship pulls in)
   * and a departure leg (what is on board when it leaves). Cargo loaded here
   * only exists on the departure leg, so at a loading call that is not the
   * first of the voyage the arrival leg exists but is empty: preferring it by
   * mere existence renders an empty cargo list.
   *
   * Only switch to departure when arrival is empty and departure has cargo, so
   * discharge calls keep showing the arrival leg as before.
   */
  public static pickDisplaySegment(
    arrival: VoyageSegmentModel | null,
    departure: VoyageSegmentModel | null
  ): VoyageSegmentModel | null {
    const arrivalIsEmpty = !!arrival && !arrival.bookingItems.length;

    if (arrivalIsEmpty && departure?.bookingItems.length) {
      return departure;
    }

    return arrival ?? departure ?? null;
  }

  public fromJSON(json: any): VoyageSegmentModel {
    if (json) {
      this.id = json.id;
      this.availableCapacity.fromJSON({});
      this.occupiedCapacity.fromJSON({});
      this.occupiedPercentage.fromJSON({});
      this.totalCapacity.fromJSON({});
      this.bookingItems = [];

      //Load capacity
      if (json.capacity) {
        this.availableCapacity.fromJSON(json.capacity?.available);
        this.occupiedCapacity.fromJSON(json.capacity?.occupied);
        this.occupiedPercentage.fromJSON(json.capacity?.occupied);
        this.totalCapacity.fromJSON(json.capacity?.total);

        //Calculate percentage
        this.recalculateOccupiedPercentage();
      }

      //Load booking items
      ArrayUtility.each(json.bookingItemSegments, (bookingItemSegment: any) => {
        const bookingItem: BookingItemModel = new BookingItemModel();
        bookingItem.fromJSON(bookingItemSegment.bookingItem);
        this.bookingItems.push(bookingItem);
      });
    }
    return this;
  }

  public fromJSONList(json: any): VoyageSegmentModel[] {
    if (json) {
      return json.map((element: any) =>
        new VoyageSegmentModel().fromJSON(element)
      );
    }
    return [];
  }

  public toJSON() {
    return {
      id: this.id,
      availableCapacity: this.availableCapacity.toJSON(),
      occupiedCapacity: this.occupiedCapacity.toJSON(),
      occupiedPercentage: this.occupiedPercentage.toJSON(),
      totalCapacity: this.totalCapacity.toJSON(),
    };
  }

  public getId() {
    return this.id ?? '';
  }

  public getReference() {
    return this.id ?? '';
  }

  public recalculateOccupiedPercentage(): void {
    let linearMeters = 0;
    let squareMeters = 0;
    let weightTons = 0;

    if (this.occupiedCapacity.linearMeters && this.totalCapacity.linearMeters) {
      linearMeters =
        this.occupiedCapacity.linearMeters / this.totalCapacity.linearMeters;
    }
    if (this.occupiedCapacity.squareMeters && this.totalCapacity.squareMeters) {
      squareMeters =
        this.occupiedCapacity.squareMeters / this.totalCapacity.squareMeters;
    }
    if (this.occupiedCapacity.weightTons && this.totalCapacity.weightTons) {
      weightTons =
        this.occupiedCapacity.weightTons / this.totalCapacity.weightTons;
    }

    this.occupiedPercentage.linearMeters = Math.min(
      100,
      Math.ceil(linearMeters * 100)
    );
    this.occupiedPercentage.squareMeters = Math.min(
      100,
      Math.ceil(squareMeters * 100)
    );
    this.occupiedPercentage.weightTons = Math.min(
      100,
      Math.ceil(weightTons * 100)
    );
  }

  // public getOccupiedLinearMetersPercentage(): number {
  //   let percentage = 0;
  //   if (!!this?.occupiedLinearMeters && !!this?.totalLinearMeters) {
  //     percentage = this.occupiedLinearMeters / this.totalLinearMeters;
  //   }

  //   return Math.min(100, Math.ceil(percentage * 100));
  // }

  // public getOccupiedWeightTonsPercentage(): number {
  //   let percentage = 0;
  //   if (!!this?.occupiedWeightTons && !!this?.totalWeightTons) {
  //     percentage = this.occupiedWeightTons / this.totalWeightTons;
  //   }

  //   return Math.min(100, Math.ceil(percentage * 100));
  // }
}
